// UI/HTML helper utilities shared across the frontend bundles.

// Convert HTML element to plain text while preserving list structure.
export function htmlToPlainText(element) {
  if (!element) return "";

  let result = "";

  function processNode(node, depth = 0) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.trim();
      if (text) return text + " ";
      return "";
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return "";

    const tagName = node.tagName?.toLowerCase();
    let content = "";

    switch (tagName) {
      case "p": {
        const pContent = Array.from(node.childNodes)
          .map((child) => processNode(child, depth))
          .join("")
          .trim();
        if (pContent) content = "\n\n" + pContent + "\n";
        break;
      }
      case "ul": {
        const ulItems = Array.from(node.children).filter(
          (child) => child.tagName?.toLowerCase() === "li"
        );
        if (ulItems.length > 0) {
          content = "\n";
          ulItems.forEach((li) => {
            const liContent = Array.from(li.childNodes)
              .map((child) => processNode(child, depth + 1))
              .join("")
              .trim();
            if (liContent) {
              const cleanContent = liContent.replace(/\n+/g, " ").trim();
              content += `• ${cleanContent}\n`;
            }
          });
          content += "\n";
        }
        break;
      }
      case "ol": {
        const olItems = Array.from(node.children).filter(
          (child) => child.tagName?.toLowerCase() === "li"
        );
        if (olItems.length > 0) {
          content = "\n";
          olItems.forEach((li, index) => {
            const liContent = Array.from(li.childNodes)
              .map((child) => processNode(child, depth + 1))
              .join("")
              .trim();
            if (liContent) {
              const cleanContent = liContent.replace(/\n+/g, " ").trim();
              content += `${index + 1}. ${cleanContent}\n`;
            }
          });
          content += "\n";
        }
        break;
      }
      case "li": {
        content = Array.from(node.childNodes)
          .map((child) => processNode(child, depth))
          .join("");
        break;
      }
      case "h1":
      case "h2":
      case "h3":
      case "h4":
      case "h5":
      case "h6": {
        const headingContent = Array.from(node.childNodes)
          .map((child) => processNode(child, depth))
          .join("")
          .trim();
        if (headingContent) content = "\n\n" + headingContent + "\n\n";
        break;
      }
      case "br":
        content = "\n";
        break;
      case "table": {
        const rows = node.querySelectorAll("tr");
        if (rows.length === 0) break;
        const tableLines = [];
        rows.forEach((tr, rowIndex) => {
          const cells = tr.querySelectorAll("th, td");
          const cellTexts = Array.from(cells).map((cell) => {
            const text = Array.from(cell.childNodes)
              .map((child) => processNode(child, depth))
              .join("")
              .trim()
              .replace(/\|/g, "\\|")
              .replace(/\n+/g, " ");
            return text;
          });
          if (cellTexts.length > 0) {
            tableLines.push("| " + cellTexts.join(" | ") + " |");
            if (rowIndex === 0) {
              tableLines.push("| " + cellTexts.map(() => "---").join(" | ") + " |");
            }
          }
        });
        if (tableLines.length > 0) {
          content = "\n\n" + tableLines.join("\n") + "\n\n";
        }
        break;
      }
      case "strong":
      case "b":
      case "em":
      case "i":
      case "a":
        content = Array.from(node.childNodes)
          .map((child) => processNode(child, depth))
          .join("");
        break;
      default:
        content = Array.from(node.childNodes)
          .map((child) => processNode(child, depth))
          .join("");
        break;
    }

    return content;
  }

  result = Array.from(element.childNodes)
    .map((child) => processNode(child, 0))
    .join("");

  result = result.replace(/\n{3,}/g, "\n\n");
  result = result.replace(/[ \t]+\n/g, "\n");
  result = result.replace(/\n[ \t]+/g, "\n");

  return result.trim();
}

// Replace timestamp-based IDs with search_id-based IDs in stored HTML blobs.
export function replaceTimestampWithSearchId(html, searchId) {
  if (!html) return "";

  let result = html;

  result = result.replace(/id=["']([^"']+)-(\d+)["']/g, (match, prefix, timestamp) => {
    if (timestamp.length >= 10) {
      return `id="${prefix}-${searchId}"`;
    }
    return match;
  });

  result = result.replace(/data-timestamp=["'](\d+)["']/g, `data-timestamp="${searchId}"`);

  result = result.replace(/onclick=["']([^"']*?)(\d{10,})([^"']*?)["']/g, (match, before, timestamp, after) => {
    return `onclick="${before}${searchId}${after}"`;
  });

  return result;
}

